import { useEffect, useRef, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  Handle,
  NodeResizeControl,
  NodeResizer,
  Position,
  useConnection,
  type EdgeProps,
  type NodeProps,
} from '@xyflow/react';
import { Building2, ChevronDown, ChevronUp, Forklift, Mail, Minus, Plus, UserRound, Users, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';

import { figureCatalogByKey } from './organogramaFigureCatalog';
import { resolveColumnTitlePosition } from './organogramaTypes';
import type {
  OrgMvpAnnotationData,
  OrgMvpCardData,
  OrgMvpCardQuickView,
  OrgMvpColumnData,
  OrgMvpFigure,
  OrgMvpFigureData,
  OrgMvpShapeData,
  OrgMvpTableData,
  OrgMvpTextData,
} from './organogramaTypes';

// Handles de resize maiores que o padrão do React Flow (que é ~6px e passa despercebido).
const RESIZER_HANDLE_CLASS = '!h-3.5 !w-3.5 !rounded-full !border-2 !border-white !shadow-md';
const RESIZER_LINE_CLASS = '!border-2 !border-dashed';

const EDGE_HANDLE_POSITIONS = ['top', 'right', 'bottom', 'left'] as const;
const EDGE_HANDLE_LONG = 32;
const EDGE_HANDLE_SHORT = 10;

/** O NodeResizer só desenha alça nos 4 cantos; estas são as alças das bordas.
 *  O tamanho vai no próprio controle (e não só no filho) porque o React Flow centraliza
 *  a alça com `translate: -50% -50%` sobre a caixa do controle — que por padrão tem 5x5px.
 *  Com a caixa menor que a alça visível, ela ficava deslocada da borda durante o resize. */
function EdgeResizeHandles({ minWidth, minHeight }: { minWidth: number; minHeight: number }) {
  return (
    <>
      {EDGE_HANDLE_POSITIONS.map((position) => {
        const isVertical = position === 'top' || position === 'bottom';
        return (
          <NodeResizeControl
            key={`edge-resize-${position}`}
            position={position}
            minWidth={minWidth}
            minHeight={minHeight}
            style={{
              background: 'transparent',
              border: 'none',
              width: isVertical ? EDGE_HANDLE_LONG : EDGE_HANDLE_SHORT,
              height: isVertical ? EDGE_HANDLE_SHORT : EDGE_HANDLE_LONG,
            }}
          >
            <span className="pointer-events-none block h-full w-full rounded-full border-2 border-white bg-stone-800 shadow-md" />
          </NodeResizeControl>
        );
      })}
    </>
  );
}

export function MvpColumnNode({ id, data, selected }: NodeProps) {
  const column = data as OrgMvpColumnData;
  const safeColumn: OrgMvpColumnData = {
    title: typeof column?.title === 'string' ? column.title : 'Coluna',
    color: typeof column?.color === 'string' ? column.color : '#E0F2FE',
    orientation: column?.orientation === 'horizontal' ? 'horizontal' : 'vertical',
    titlePosition: column?.titlePosition,
    onChange: column?.onChange,
    onEditEnd: column?.onEditEnd,
    readonly: column?.readonly,
  };
  const titlePosition = resolveColumnTitlePosition(safeColumn);
  const isTitleOnSide = titlePosition === 'left';

  const titleField = (
    <div className={cn(isTitleOnSide && 'flex w-12 shrink-0 items-center justify-center')}>
      <Input
        className={cn(
          'nodrag h-9 rounded-full border-stone-300 bg-white/75 text-center text-sm font-semibold text-stone-900 shadow-sm',
          isTitleOnSide && 'h-10 w-[180px] -rotate-90',
        )}
        value={safeColumn.title}
        onChange={(event) => safeColumn.onChange?.(id, { title: event.target.value })}
        onBlur={() => safeColumn.onEditEnd?.(id)}
      />
    </div>
  );

  return (
    <div
      className={cn(
        'relative flex h-full w-full gap-4 rounded-[28px] border-2 border-dashed border-stone-300/80 p-4 shadow-sm',
        isTitleOnSide ? 'flex-row items-stretch' : 'flex-col',
      )}
      style={{ backgroundColor: withAlpha(safeColumn.color, 0.72) }}
    >
      {!safeColumn.readonly && (
        <>
          <NodeResizer
            isVisible={selected}
            minWidth={240}
            minHeight={260}
            lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')}
            handleClassName={cn(RESIZER_HANDLE_CLASS, '!bg-stone-800')}
          />
          {selected && <EdgeResizeHandles minWidth={240} minHeight={260} />}
        </>
      )}
      {titlePosition !== 'bottom' && titleField}
      {/* Espaçador invisível: só reserva o espaço pro conteúdo da coluna, sem desenhar uma
          segunda caixa por cima da borda tracejada externa (pedido do Lucas, 08/09). */}
      <div className="min-h-0 min-w-0 flex-1" />
      {titlePosition === 'bottom' && titleField}
    </div>
  );
}

export function MvpCardNode({ id, data, selected }: NodeProps) {
  const card = data as OrgMvpCardData;
  const connection = useConnection();
  const isConnectionTarget =
    connection.inProgress && connection.toNode?.id === id && connection.fromNode?.id !== id;
  const connectionRing = isConnectionTarget
    ? connection.isValid === false
      ? '0 0 0 4px rgba(220, 38, 38, 0.4), 0 18px 35px rgba(15, 23, 42, 0.14)'
      : '0 0 0 4px rgba(16, 185, 129, 0.5), 0 18px 35px rgba(15, 23, 42, 0.14)'
    : null;
  const highlightRing = card?.highlighted
    ? '0 0 0 5px rgba(245, 158, 11, 0.55), 0 18px 35px rgba(15, 23, 42, 0.14)'
    : null;
  const quickView = card?.quickView as OrgMvpCardQuickView | undefined;
  const hasChildren = card?.hasChildren === true;
  const isCollapsed = card?.isCollapsed === true;
  const directReports = typeof card?.directReports === 'number' ? card.directReports : 0;
  const collapsedCount = typeof card?.collapsedCount === 'number' ? card.collapsedCount : 0;
  const safeCard: OrgMvpCardData = {
    kind: card?.kind === 'department' ? 'department' : 'person',
    label: typeof card?.label === 'string' ? card.label : '',
    subtitle: typeof card?.subtitle === 'string' ? card.subtitle : '',
    description: typeof card?.description === 'string' ? card.description : '',
    color: typeof card?.color === 'string' ? card.color : '#2563EB',
    textColor: typeof card?.textColor === 'string' ? card.textColor : '#0F172A',
    figure: card?.figure as OrgMvpCardData['figure'],
  };
  const safeFigure: OrgMvpFigure = safeCard.figure && (safeCard.figure.type === 'catalog' || safeCard.figure.type === 'upload')
    ? safeCard.figure
    : { type: 'catalog', key: 'person' };
  const FigureIcon = safeFigure.type === 'catalog' ? figureCatalogByKey.get(safeFigure.key)?.icon || UserRound : null;

  const cardBody = (
    <div
      className="relative h-full w-full rounded-3xl border bg-white p-4 shadow-lg transition hover:shadow-xl"
      style={{
        borderColor: isConnectionTarget
          ? (connection.isValid === false ? '#DC2626' : '#10B981')
          : withAlpha(safeCard.color, 0.38),
        boxShadow: connectionRing
          || highlightRing
          || (selected ? `0 0 0 4px ${withAlpha(safeCard.color, 0.22)}, 0 18px 35px rgba(15, 23, 42, 0.14)` : undefined),
        opacity: card?.dimmed ? 0.25 : undefined,
      }}
    >
      {!card?.readonly && (
        <NodeResizer
          isVisible={selected}
          minWidth={170}
          minHeight={92}
          lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')}
          handleClassName={RESIZER_HANDLE_CLASS}
          handleStyle={{ backgroundColor: safeCard.color }}
        />
      )}
      <div className="flex items-start gap-3">
        {/* Alvo de clique próprio: `stopPropagation` no pointerdown e no click impede que o
            React Flow trate isto como clique no card e abra o painel de propriedades normal.
            Sem isso os dois comportamentos disparavam juntos. */}
        <button
          type="button"
          className={cn(
            'nodrag nopan flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-white shadow-sm',
            !card?.readonly && 'cursor-pointer transition hover:ring-2 hover:ring-white/70 hover:brightness-110',
          )}
          style={{ backgroundColor: safeCard.color }}
          title={card?.readonly ? undefined : 'Clique para trocar o ícone'}
          onPointerDown={(event) => {
            if (card?.readonly) return;
            event.stopPropagation();
          }}
          onClick={(event) => {
            if (card?.readonly) return;
            event.stopPropagation();
            event.preventDefault();
            card?.onFigureClick?.(id);
          }}
        >
          {safeFigure.type === 'upload' ? (
            <img src={safeFigure.url} alt={safeFigure.name} className="h-full w-full bg-white object-contain p-1" />
          ) : FigureIcon ? (
            <FigureIcon className="h-5 w-5" />
          ) : null}
        </button>
        <div className="min-w-0 flex-1">
          <InlineLabel
            value={safeCard.label}
            color={safeCard.textColor}
            readonly={card?.readonly}
            onCommit={(label) => card?.onChange?.(id, { label })}
            onEditEnd={() => card?.onEditEnd?.(id)}
          />
          <div className="mt-1 line-clamp-2 text-xs text-stone-500">{safeCard.subtitle}</div>
          {typeof (card as { cronogramaTitulo?: unknown }).cronogramaTitulo === 'string' && (card as { cronogramaTitulo?: string }).cronogramaTitulo && (
            <div className="mt-2 inline-flex rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
              {(card as { cronogramaTitulo: string }).cronogramaTitulo}
            </div>
          )}
          {safeCard.description && (
            <div className="mt-2 line-clamp-2 rounded-xl bg-stone-50 px-2 py-1 text-[11px] text-stone-500">{safeCard.description}</div>
          )}
        </div>
      </div>

      {(['top', 'right', 'bottom', 'left'] as const).map((position) => (
        <Handle
          key={`source-${position}`}
          type="source"
          id={position}
          position={positionToReactFlow(position)}
          className="!h-3 !w-3 !border-2 !border-white"
          style={{ backgroundColor: safeCard.color }}
        />
      ))}
      {(['top', 'right', 'bottom', 'left'] as const).map((position) => (
        <Handle
          key={`target-${position}`}
          type="target"
          id={`t-${position}`}
          position={positionToReactFlow(position)}
          className="!h-4 !w-4 !border-0 !bg-transparent"
        />
      ))}

      {hasChildren && (
        <button
          type="button"
          className="nodrag nopan absolute -bottom-3.5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold shadow-sm transition hover:scale-105"
          style={{ borderColor: withAlpha(safeCard.color, 0.5), color: safeCard.color }}
          onClick={(event) => {
            event.stopPropagation();
            card?.onToggleCollapse?.(id);
          }}
          title={isCollapsed ? `Expandir ${collapsedCount} subordinado(s)` : 'Recolher subordinados'}
        >
          {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          {isCollapsed ? `+${collapsedCount}` : directReports}
        </button>
      )}
    </div>
  );

  if (!quickView) return cardBody;

  return (
    <HoverCard openDelay={350} closeDelay={80}>
      <HoverCardTrigger asChild>{cardBody}</HoverCardTrigger>
      <HoverCardContent side="top" align="center" className="w-64 space-y-2 rounded-2xl p-4 text-left">
        <div className="text-sm font-bold text-foreground">{quickView.nome}</div>
        {quickView.cargo && <div className="text-xs text-muted-foreground">{quickView.cargo}</div>}
        <div className="space-y-1.5 border-t pt-2 text-xs text-muted-foreground">
          {quickView.departamento && (
            <div className="flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{quickView.departamento}</span>
            </div>
          )}
          {quickView.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{quickView.email}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5 shrink-0" />
            <span>{directReports} report(s) direto(s)</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export function MvpFigureNode({ data, selected }: NodeProps) {
  const figure = data as OrgMvpFigureData;
  const safeFigureData: OrgMvpFigureData = {
    label: typeof figure?.label === 'string' ? figure.label : 'Figura',
    color: typeof figure?.color === 'string' ? figure.color : '#EA580C',
    showBackground: figure?.showBackground !== false,
    figure: figure?.figure as OrgMvpFigureData['figure'],
  };
  const safeFigure: OrgMvpFigure = safeFigureData.figure && (safeFigureData.figure.type === 'catalog' || safeFigureData.figure.type === 'upload')
    ? safeFigureData.figure
    : { type: 'catalog', key: 'forklift' };
  const FigureIcon = safeFigure.type === 'catalog' ? figureCatalogByKey.get(safeFigure.key)?.icon || Forklift : null;

  return (
    <div
      className={cn(
        'relative flex h-full w-full items-center justify-center overflow-visible',
        safeFigureData.showBackground && 'rounded-3xl border bg-white p-3 shadow-lg',
      )}
      style={{
        borderColor: safeFigureData.showBackground ? withAlpha(safeFigureData.color, 0.38) : undefined,
        boxShadow: selected && !safeFigureData.showBackground ? `0 0 0 4px ${withAlpha(safeFigureData.color, 0.18)}` : undefined,
      }}
      title={safeFigureData.label}
    >
      {!figure?.readonly && (
        <NodeResizer
          isVisible={selected}
          minWidth={36}
          minHeight={36}
          lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')}
          handleClassName={RESIZER_HANDLE_CLASS}
          handleStyle={{ backgroundColor: safeFigureData.color }}
        />
      )}

      {safeFigure.type === 'upload' ? (
        <img src={safeFigure.url} alt={safeFigure.name} className="h-full w-full object-contain" />
      ) : FigureIcon ? (
        <FigureIcon className="h-full w-full" strokeWidth={1.75} style={{ color: safeFigureData.color }} />
      ) : null}

      {(['top', 'right', 'bottom', 'left'] as const).map((position) => (
        <Handle
          key={`source-${position}`}
          type="source"
          id={position}
          position={positionToReactFlow(position)}
          className="!h-3 !w-3 !border-2 !border-white"
          style={{ backgroundColor: safeFigureData.color }}
        />
      ))}
      {(['top', 'right', 'bottom', 'left'] as const).map((position) => (
        <Handle
          key={`target-${position}`}
          type="target"
          id={`t-${position}`}
          position={positionToReactFlow(position)}
          className="!h-4 !w-4 !border-0 !bg-transparent"
        />
      ))}
    </div>
  );
}

export function MvpTextNode({ id, data, selected }: NodeProps) {
  const text = data as OrgMvpTextData;
  const safe: OrgMvpTextData = {
    text: typeof text?.text === 'string' ? text.text : '',
    color: typeof text?.color === 'string' ? text.color : '#0F172A',
    fontSize: typeof text?.fontSize === 'number' ? text.fontSize : 16,
    onChange: text?.onChange,
    readonly: text?.readonly,
  };

  return (
    <div className="relative h-full w-full">
      {!safe.readonly && (
        <NodeResizer isVisible={selected} minWidth={80} minHeight={32} lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')} handleClassName={cn(RESIZER_HANDLE_CLASS, '!bg-stone-800')} />
      )}
      <textarea
        className="nodrag h-full w-full resize-none border-none bg-transparent p-1 outline-none placeholder:text-muted-foreground/60"
        style={{ color: safe.color, fontSize: safe.fontSize }}
        value={safe.text}
        placeholder="Texto..."
        readOnly={safe.readonly}
        onChange={(event) => safe.onChange?.(id, { text: event.target.value })}
      />
    </div>
  );
}

export function MvpAnnotationNode({ id, data, selected }: NodeProps) {
  const note = data as OrgMvpAnnotationData;
  const safe: OrgMvpAnnotationData = {
    text: typeof note?.text === 'string' ? note.text : '',
    color: typeof note?.color === 'string' ? note.color : '#FEF08A',
    onChange: note?.onChange,
    readonly: note?.readonly,
  };

  return (
    <div className="relative h-full w-full">
      {!safe.readonly && (
        <NodeResizer isVisible={selected} minWidth={120} minHeight={100} lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')} handleClassName={cn(RESIZER_HANDLE_CLASS, '!bg-stone-800')} />
      )}
      <div
        className="relative h-full w-full p-3 shadow-md"
        style={{
          backgroundColor: safe.color,
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 22px, 22px 22px, 22px 0)',
        }}
      >
        <div
          className="absolute left-0 top-0 h-[22px] w-[22px]"
          style={{ backgroundColor: withAlpha('#000000', 0.12), clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
        />
        <textarea
          className="nodrag h-full w-full resize-none border-none bg-transparent pt-2 text-sm text-stone-900 outline-none placeholder:text-stone-900/50"
          value={safe.text}
          placeholder="Anotação..."
          readOnly={safe.readonly}
          onChange={(event) => safe.onChange?.(id, { text: event.target.value })}
        />
      </div>
    </div>
  );
}

export function MvpTableNode({ id, data, selected }: NodeProps) {
  const table = data as OrgMvpTableData;
  const rows = Array.isArray(table?.rows) && table.rows.length > 0 ? table.rows : [['', ''], ['', '']];
  const readonly = Boolean(table?.readonly);
  const onChange = table?.onChange;

  const updateCell = (rowIndex: number, colIndex: number, value: string) => {
    const next = rows.map((row) => [...row]);
    next[rowIndex][colIndex] = value;
    onChange?.(id, { rows: next });
  };

  const addRow = () => {
    const cols = rows[0]?.length || 2;
    onChange?.(id, { rows: [...rows, Array.from({ length: cols }, () => '')] });
  };

  const addColumn = () => {
    onChange?.(id, { rows: rows.map((row) => [...row, '']) });
  };

  const removeRow = (rowIndex: number) => {
    if (rows.length <= 1) return;
    onChange?.(id, { rows: rows.filter((_, i) => i !== rowIndex) });
  };

  const removeColumn = (colIndex: number) => {
    if ((rows[0]?.length || 0) <= 1) return;
    onChange?.(id, { rows: rows.map((row) => row.filter((_, i) => i !== colIndex)) });
  };

  return (
    <div className="relative h-full w-full">
      {!readonly && (
        <NodeResizer isVisible={selected} minWidth={180} minHeight={100} lineClassName={cn(RESIZER_LINE_CLASS, '!border-primary')} handleClassName={cn(RESIZER_HANDLE_CLASS, '!border-background !bg-primary')} />
      )}
      <div className="h-full w-full overflow-auto rounded-md border border-border bg-card shadow-sm">
        <table className="w-full border-collapse text-xs text-foreground">
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={cn('group/row', rowIndex === 0 && 'bg-muted/60')}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} className="border border-border p-0">
                    <input
                      className={cn(
                        'nodrag h-8 w-full min-w-[70px] border-none bg-transparent px-2 text-foreground outline-none placeholder:text-muted-foreground',
                        rowIndex === 0 && 'font-semibold',
                      )}
                      value={cell}
                      readOnly={readonly}
                      onChange={(event) => updateCell(rowIndex, colIndex, event.target.value)}
                    />
                  </td>
                ))}
                {!readonly && selected && rows.length > 1 && (
                  <td className="w-6 border-none p-0 align-middle">
                    <button
                      type="button"
                      className="nodrag flex h-6 w-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover/row:opacity-100"
                      title="Remover linha"
                      onClick={() => removeRow(rowIndex)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readonly && selected && (
        <div className="mt-1 flex gap-1">
          <button type="button" className="nodrag flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground shadow-sm hover:bg-muted" onClick={addRow}>
            <Plus className="h-3 w-3" /> Linha
          </button>
          <button type="button" className="nodrag flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground shadow-sm hover:bg-muted" onClick={addColumn}>
            <Plus className="h-3 w-3" /> Coluna
          </button>
          {(rows[0]?.length || 0) > 1 && (
            <button
              type="button"
              className="nodrag flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground shadow-sm hover:bg-muted"
              onClick={() => removeColumn((rows[0]?.length || 1) - 1)}
            >
              <Minus className="h-3 w-3" /> Coluna
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Rename-in-place: um clique no nome do card troca o texto por um input, sem passar pelo painel. */
function InlineLabel({
  value,
  color,
  readonly,
  onCommit,
  onEditEnd,
}: {
  value: string;
  color: string;
  readonly?: boolean;
  onCommit: (label: string) => void;
  onEditEnd: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const finish = () => {
    setEditing(false);
    onEditEnd();
  };

  if (editing && !readonly) {
    return (
      <input
        ref={inputRef}
        autoFocus
        className="nodrag w-full rounded-md border border-stone-300 bg-white px-1.5 py-0.5 text-sm font-bold outline-none focus:border-stone-500"
        style={{ color }}
        value={value}
        onChange={(event) => onCommit(event.target.value)}
        onBlur={finish}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === 'Escape') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
    );
  }

  return (
    <div
      // Nome longo quebra linha em vez de cortar logo na 1ª (27/10/2026); acima de 3 linhas corta, para não vazar do
      // card (altura fixa). O nome inteiro fica no tooltip.
      className={cn('line-clamp-3 break-words text-sm font-bold leading-snug [overflow-wrap:anywhere]', !readonly && 'nodrag cursor-text')}
      style={{ color }}
      title={readonly ? value : `${value} — clique para renomear`}
      onClick={() => {
        if (!readonly) setEditing(true);
      }}
    >
      {value}
    </div>
  );
}

export function MvpShapeNode({ data, selected }: NodeProps) {
  const shape = data as OrgMvpShapeData;
  const variant: OrgMvpShapeData['shape'] =
    shape?.shape === 'circle' || shape?.shape === 'line' ? shape.shape : 'square';
  const color = typeof shape?.color === 'string' ? shape.color : '#334155';
  const strokeWidth = typeof shape?.strokeWidth === 'number' ? shape.strokeWidth : 2;
  const fill = typeof shape?.fill === 'string' ? shape.fill : 'transparent';
  const isLine = variant === 'line';
  const isDashed = shape?.dashed === true;

  return (
    <div className="relative h-full w-full">
      {!shape?.readonly && (
        <>
          <NodeResizer
            isVisible={selected}
            minWidth={24}
            minHeight={isLine ? 4 : 24}
            lineClassName={cn(RESIZER_LINE_CLASS, '!border-stone-700')}
            handleClassName={RESIZER_HANDLE_CLASS}
            handleStyle={{ backgroundColor: color }}
          />
          {selected && <EdgeResizeHandles minWidth={24} minHeight={isLine ? 4 : 24} />}
        </>
      )}

      {isLine ? (
        <svg className="h-full w-full overflow-visible" preserveAspectRatio="none">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={isDashed ? '10 8' : undefined}
          />
        </svg>
      ) : (
        <div
          className={cn('h-full w-full', variant === 'circle' ? 'rounded-full' : 'rounded-md')}
          style={{
            border: `${strokeWidth}px ${isDashed ? 'dashed' : 'solid'} ${color}`,
            backgroundColor: fill,
          }}
        />
      )}

      {EDGE_HANDLE_POSITIONS.map((position) => (
        <Handle
          key={`source-${position}`}
          type="source"
          id={position}
          position={positionToReactFlow(position)}
          className="!h-3 !w-3 !border-2 !border-white"
          style={{ backgroundColor: color }}
        />
      ))}
      {EDGE_HANDLE_POSITIONS.map((position) => (
        <Handle
          key={`target-${position}`}
          type="target"
          id={`t-${position}`}
          position={positionToReactFlow(position)}
          className="!h-4 !w-4 !border-0 !bg-transparent"
        />
      ))}
    </div>
  );
}

function positionToReactFlow(position: 'top' | 'right' | 'bottom' | 'left') {
  if (position === 'top') return Position.Top;
  if (position === 'right') return Position.Right;
  if (position === 'bottom') return Position.Bottom;
  return Position.Left;
}

function normalizeColorInput(color: unknown) {
  if (typeof color !== 'string') return '#2563EB';
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '#2563EB';
}

function withAlpha(hex: string, alpha: number) {
  const normalized = normalizeColorInput(hex).replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Uma única implementação registrada pros 3 tipos de linha (`default`, `smoothstep`,
 *  `straight`) — o formato do traçado sai de `props.type`, então o toolbar de "Forma da
 *  linha" continua funcionando exatamente como antes. O que essa versão acrescenta é o "+"
 *  no meio da linha (pedido do Lucas, 08/09), pra inserir um card entre dois já conectados
 *  sem precisar desenhar duas conexões novas na mão. */
export function MvpEdge({
  id,
  type,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  selected,
  data,
}: EdgeProps) {
  const [hovered, setHovered] = useState(false);
  const edgeData = data as { onInsertNode?: (edgeId: string, midX: number, midY: number) => void; readonly?: boolean } | undefined;

  const pathArgs = { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition };
  const [path, labelX, labelY] =
    type === 'smoothstep'
      ? getSmoothStepPath(pathArgs)
      : type === 'straight'
        ? getStraightPath(pathArgs)
        : getBezierPath(pathArgs);

  const showButton = !edgeData?.readonly && Boolean(edgeData?.onInsertNode) && (hovered || selected);

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={style}
        markerEnd={markerEnd}
        interactionWidth={26}
      />
      {/* Faixa invisível maior que a linha, só pra facilitar entrar em hover nela. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      />
      {showButton && (
        <EdgeLabelRenderer>
          <button
            type="button"
            className="nodrag nopan absolute flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-stone-800 text-white shadow-md transition hover:scale-110 hover:bg-stone-900"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            onClick={(event) => {
              event.stopPropagation();
              edgeData?.onInsertNode?.(id, labelX, labelY);
            }}
            title="Adicionar um card aqui no meio"
            aria-label="Adicionar um card no meio da linha"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
